import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  ExpandedState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { DataTableToolbar } from "./DataTableToolbar";
import { SortableHeaderCell } from "./SortableHeaderCell";
import { TableDensity, useTableViewState } from "./useTableViewState";
import "./types"; // registers the ColumnMeta<filterVariant/filterOptions/excludeFromExport> augmentation

type DataTableProps<T> = {
  // `any` here (rather than `unknown`) sidesteps TS variance issues when a caller
  // builds a heterogeneous columns array (e.g. via conditional spreads) with createColumnHelper.
  // Each column's standard TanStack `size`/`minSize`/`maxSize` sets its initial/fixed width;
  // it only takes effect once `enableColumnResizing` or `enableColumnPinning` is on (see
  // `useFixedLayout` below) — otherwise columns size to their content.
  columns: ColumnDef<T, any>[];
  data: T[];
  /** Falls back to the row's index in `data` when this returns a falsy value, to keep row IDs unique. */
  getRowId?: (row: T, index: number) => string;
  emptyMessage?: string;
  /** Suppresses the empty state while data is still being fetched. */
  isLoading?: boolean;
  /** Per-column filter inputs, driven by each column's `meta.filterVariant`. */
  enableColumnFilters?: boolean;
  /** Single search box filtering across all columns of the loaded `data`. */
  enableGlobalFilter?: boolean;
  /** "Columns" menu for showing/hiding columns (columns need `enableHiding: false` to be pinned visible). */
  enableColumnVisibility?: boolean;
  /** Default shown/hidden state for columns when no saved `columnVisibility` exists yet (keyed by column id). Saved URL/localStorage visibility overrides this. */
  initialColumnVisibility?: VisibilityState;
  /** Drag-to-resize column widths. */
  enableColumnResizing?: boolean;
  /** Allows fixed-width cells to wrap long values rather than truncate them. */
  enableCellWrapping?: boolean;
  /** Drag-and-drop column reordering via a per-header drag handle. */
  enableColumnOrdering?: boolean;
  /** Per-header pin toggle that freezes a column to the left while scrolling horizontally. */
  enableColumnPinning?: boolean;
  /** Compact/comfortable row-height toggle in the toolbar. */
  enableDensityToggle?: boolean;
  /** Sticks the header to the top of the table's scroll area (which becomes height-bounded). */
  enableStickyHeader?: boolean;
  /** Applies a row-hover background without changing the default shared table appearance. */
  enableRowHover?: boolean;
  /** Persists sort/filters/visibility/order/sizing/pinning/density to localStorage under this key. Give each logical table a unique, stable key. */
  persistStateKey?: string;
  /** Also encodes the same view state into a `dt.<persistStateKey>` URL param, so a shared link reproduces the sender's view. Requires `persistStateKey`. */
  enableUrlSync?: boolean;
  /** Renders an expandable sub-row for a row. Providing this adds an expand/collapse column. */
  renderSubRow?: (row: T) => ReactNode;
  getRowCanExpand?: (row: T) => boolean;
};

const DEBOUNCE_MS = 300;
// Pinned cells need an explicit opaque background, so this matches the native striped-row color.
const TABLE_STRIPE_COLOR = "#f9f9f9";

// Headless TanStack Table. Sorting/filtering/faceting all operate on whatever `data` is passed in
// (client-side, current page only for server-paginated tables). Note: only flat (non-grouped)
// column headers are supported.
export function DataTable<T>({
  columns,
  data,
  getRowId,
  emptyMessage = "No results.",
  isLoading = false,
  enableColumnFilters,
  enableGlobalFilter,
  enableColumnVisibility,
  initialColumnVisibility,
  enableColumnResizing,
  enableCellWrapping,
  enableColumnOrdering,
  enableColumnPinning,
  enableDensityToggle,
  enableStickyHeader,
  enableRowHover,
  persistStateKey,
  enableUrlSync,
  renderSubRow,
  getRowCanExpand,
}: DataTableProps<T>) {
  const { initial, persist } = useTableViewState(
    persistStateKey,
    enableUrlSync,
  );

  const [sorting, setSorting] = useState<SortingState>(initial.sorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initial.columnFilters ?? [],
  );
  const [globalFilter, setGlobalFilter] = useState<string>(
    initial.globalFilter ?? "",
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initial.columnVisibility ?? initialColumnVisibility ?? {},
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    initial.columnOrder ?? [],
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    initial.columnSizing ?? {},
  );
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    initial.columnPinning ?? {},
  );
  const [density, setDensity] = useState<TableDensity>(
    initial.density ?? "comfortable",
  );
  const [expanded, setExpanded] = useState<ExpandedState>({});

  useEffect(() => {
    const timeout = setTimeout(() => {
      persist({
        sorting,
        columnFilters,
        globalFilter,
        columnVisibility,
        columnOrder,
        columnSizing,
        columnPinning,
        density,
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [
    sorting,
    columnFilters,
    globalFilter,
    columnVisibility,
    columnOrder,
    columnSizing,
    columnPinning,
    density,
    persist,
  ]);

  const effectiveColumns = useMemo<ColumnDef<T, any>[]>(() => {
    if (!renderSubRow) return columns;
    const expanderColumn: ColumnDef<T, any> = {
      id: "__expander__",
      header: () => null,
      cell: (cellProps) =>
        cellProps.row.getCanExpand() ? (
          <button
            type="button"
            onClick={cellProps.row.getToggleExpandedHandler()}
            aria-label={
              cellProps.row.getIsExpanded() ? "Collapse row" : "Expand row"
            }
            aria-expanded={cellProps.row.getIsExpanded()}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              padding: 0,
            }}
          >
            {cellProps.row.getIsExpanded() ? "\u25BE" : "\u25B8"}
          </button>
        ) : null,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enableResizing: false,
      size: 32,
    };
    return [expanderColumn, ...columns];
  }, [columns, renderSubRow]);

  const table = useReactTable({
    data,
    columns: effectiveColumns,
    getRowId: getRowId
      ? (row: T, index: number) => getRowId(row, index) || String(index)
      : undefined,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnSizing,
      columnPinning,
      expanded,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnPinningChange: setColumnPinning,
    onExpandedChange: setExpanded,
    getRowCanExpand: getRowCanExpand
      ? (row) => getRowCanExpand(row.original)
      : undefined,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), // sorts the rows currently in `data` (client-side)
    getFilteredRowModel: getFilteredRowModel(), // column + global filters (client-side)
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(), // powers "select" filter option lists
    getExpandedRowModel: getExpandedRowModel(),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const currentOrder = table
      .getVisibleLeafColumns()
      .map((column) => column.id);
    const oldIndex = currentOrder.indexOf(active.id as string);
    const newIndex = currentOrder.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    setColumnOrder(arrayMove(currentOrder, oldIndex, newIndex));
  };

  const resetToDefaults = () => {
    setSorting([]);
    setColumnFilters([]);
    setGlobalFilter("");
    setColumnVisibility(initialColumnVisibility ?? {});
    setColumnOrder([]);
    setColumnSizing({});
    setColumnPinning({});
    setDensity("comfortable");
  };

  const headerGroup = table.getHeaderGroups()[0];
  const sortableIds = headerGroup
    ? headerGroup.headers.map((h) => h.column.id)
    : [];
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const cellPadding = density === "compact" ? "4px 8px" : undefined;
  // Pinned columns need the DOM width to match TanStack's size model (used for sticky left/right
  // offsets), so fixed layout is required whenever pinning is on, not just when resizing is on.
  const useFixedLayout = enableColumnResizing || enableColumnPinning;
  const shouldWrapFixedWidthCells = useFixedLayout && enableCellWrapping;
  // Sticky offsets (getStart/getAfter) are derived from TanStack's nominal column sizes, so only
  // stretch the table to fill extra container width when no column is actually pinned right now.
  const hasPinnedColumns =
    (columnPinning.left?.length ?? 0) > 0 ||
    (columnPinning.right?.length ?? 0) > 0;
  const useStretch = useFixedLayout && !hasPinnedColumns;

  return (
    <div>
      <DataTableToolbar
        table={table}
        enableGlobalFilter={enableGlobalFilter}
        enableColumnVisibility={enableColumnVisibility}
        enableDensityToggle={enableDensityToggle}
        density={density}
        onDensityChange={setDensity}
        onResetDefaults={enableColumnVisibility ? resetToDefaults : undefined}
      />
      <div
        style={{
          overflowX: "auto",
          overflowY: enableStickyHeader ? "auto" : undefined,
          maxHeight: enableStickyHeader ? "70vh" : undefined,
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table
            className={`w-full border-collapse bg-transparent text-left text-sm text-slate-700 [&_th]:border-b-2 [&_th]:border-slate-300 [&_th]:p-2 [&_th]:align-top [&_th]:font-semibold [&_td]:border-t [&_td]:border-slate-300 [&_td]:p-2 [&_td]:align-top [&_tbody>tr:nth-of-type(odd)]:bg-slate-50 ${
              enableRowHover ? "[&_tbody>tr:hover]:bg-slate-50" : ""
            }`}
            style={{
              width: useStretch
                ? "100%"
                : useFixedLayout
                  ? table.getTotalSize()
                  : undefined,
              minWidth: useStretch ? table.getTotalSize() : undefined,
              tableLayout: useFixedLayout ? "fixed" : undefined,
            }}
          >
            <thead>
              <SortableContext
                items={sortableIds}
                strategy={horizontalListSortingStrategy}
              >
                <tr>
                  {(headerGroup?.headers ?? []).map((header) => (
                    <SortableHeaderCell
                      key={header.id}
                      header={header}
                      enableColumnOrdering={enableColumnOrdering}
                      enableColumnResizing={enableColumnResizing}
                      enableColumnFilters={enableColumnFilters}
                      enableColumnPinning={enableColumnPinning}
                      enableStickyHeader={enableStickyHeader}
                      density={density}
                    />
                  ))}
                </tr>
              </SortableContext>
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 && !isLoading ? (
                <tr>
                  <td
                    colSpan={visibleColumnCount}
                    style={{ textAlign: "center" }}
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <tr>
                      {row.getVisibleCells().map((cell) => {
                        const pinnedSide = cell.column.getIsPinned();
                        // Pinned cells need an opaque background to match - a sticky cell
                        // paints over whatever scrolls underneath it, so without this the
                        // scrolling column's text bleeds through the pinned one.
                        const pinnedBackground = pinnedSide
                          ? row.index % 2 === 0
                            ? TABLE_STRIPE_COLOR
                            : "#fff"
                          : undefined;
                        const rawValue = cell.getValue();
                        const cellTitle =
                          typeof rawValue === "string" ||
                          typeof rawValue === "number"
                            ? String(rawValue)
                            : undefined;
                        return (
                          <td
                            key={cell.id}
                            title={cellTitle}
                            style={{
                              width: useFixedLayout
                                ? cell.column.getSize()
                                : undefined,
                              position: pinnedSide ? "sticky" : undefined,
                              left:
                                pinnedSide === "left"
                                  ? cell.column.getStart("left")
                                  : undefined,
                              right:
                                pinnedSide === "right"
                                  ? cell.column.getAfter("right")
                                  : undefined,
                              zIndex: pinnedSide ? 1 : undefined,
                              background: pinnedBackground,
                              padding: cellPadding,
                              // A fixed width doesn't clip on its own; long unbroken content (a
                              // GUID, say) would otherwise bleed into the next column.
                              overflow:
                                useFixedLayout && !shouldWrapFixedWidthCells
                                  ? "hidden"
                                  : undefined,
                              textOverflow: useFixedLayout
                                ? shouldWrapFixedWidthCells
                                  ? undefined
                                  : "ellipsis"
                                : undefined,
                              whiteSpace: shouldWrapFixedWidthCells
                                ? "normal"
                                : useFixedLayout
                                  ? "nowrap"
                                  : undefined,
                              wordBreak: shouldWrapFixedWidthCells
                                ? "break-word"
                                : undefined,
                              overflowWrap: shouldWrapFixedWidthCells
                                ? "anywhere"
                                : undefined,
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {row.getIsExpanded() ? (
                      <tr>
                        <td
                          colSpan={visibleColumnCount}
                          style={{ background: "#fafafa" }}
                        >
                          {renderSubRow?.(row.original)}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
}
